import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from 'recharts'
import type { CurrencyStrengthResult } from '../../types'

interface Props {
  data:     CurrencyStrengthResult | null
  loading?: boolean
}

// スコアに応じた色（強=緑、弱=赤、中間=青）
function scoreColor(score: number): string {
  if (score >= 0.65) return '#22c55e'   // 強い
  if (score <= 0.35) return '#ef4444'   // 弱い
  return '#3b82f6'                       // 中立
}

const CCY_JA: Record<string, string> = {
  USD: '米ドル',
  EUR: 'ユーロ',
  JPY: '円',
  GBP: '英ポンド',
  AUD: '豪ドル',
  CAD: '加ドル',
}

export default function CurrencyStrengthChart({ data, loading }: Props) {
  if (loading) {
    return (
      <div className="h-48 bg-trading-border rounded-lg animate-pulse" />
    )
  }

  if (!data?.rankings?.length) {
    return (
      <div className="flex items-center justify-center h-40 text-trading-muted text-sm">
        FX データ取得中...
      </div>
    )
  }

  const chartData = data.rankings.map((r) => ({
    currency: r.currency,
    label:    CCY_JA[r.currency] ?? r.currency,
    score:    r.score,
    pct:      Math.round(r.score * 100),
  }))

  return (
    <div className="space-y-3">
      {/* バーチャート */}
      <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 32)}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 4, right: 40, left: 4, bottom: 4 }}
        >
          <XAxis type="number" domain={[0, 1]} tick={{ fill: '#6b7a99', fontSize: 10 }} tickFormatter={(v) => `${Math.round(v * 100)}`} />
          <YAxis type="category" dataKey="currency" interval={0} tick={{ fill: '#c9d1e8', fontSize: 11, fontWeight: 600 }} width={36} />
          <Tooltip
            contentStyle={{ backgroundColor: '#1a1f2e', border: '1px solid #2a3040', borderRadius: '6px', fontSize: '11px' }}
            formatter={(_v, _name, entry) => [
              `${entry?.payload?.pct ?? 0}点`,
              entry?.payload?.label ?? '',
            ]}
            labelFormatter={() => '通貨強度'}
          />
          <Bar dataKey="score" radius={[0, 4, 4, 0]}>
            {chartData.map((entry) => (
              <Cell key={entry.currency} fill={scoreColor(entry.score)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* ランキング表示 */}
      <div className="grid grid-cols-3 gap-2">
        {chartData.slice(0, 6).map((c, i) => (
          <div
            key={c.currency}
            className="rounded border border-trading-border bg-trading-panel p-2 flex items-center gap-2"
          >
            <span className="text-[10px] text-trading-muted w-4">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-trading-text">{c.currency}</p>
              <p className="text-[9px] text-trading-muted">{c.label}</p>
            </div>
            <span
              className="text-xs font-mono font-semibold"
              style={{ color: scoreColor(c.score) }}
            >
              {c.pct}
            </span>
          </div>
        ))}
      </div>

      <p className="text-[10px] text-trading-muted text-right">
        ROC({data.period}期間) ベース・0〜100 スコア
      </p>
    </div>
  )
}
